import { useContext } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import env from "react-dotenv";

import VStack from "shared/components/layout/stack/VStack";
import Dropdown from "shared/components/inputs/form-fields/Dropdown";
import Form from "shared/components/inputs/form/Form";
import ModalActions from "shared/components/feedback/modals/ModalActions";
import { sendNotification } from "shared/components/feedback/notification/notification";
import { getDropdownOptionsNoDefaultVal } from "shared/helper-functions/dropdown/dropdownHelpers";
import ContactCard from "shared/components/data-display/drawers/ContactCard";

import { useProjectByIdDetails } from "features/projects/project-dashboard/single/useProjectsDashboardNotes";
import useFilters from "features/admin/projects/useFilters";
import useRetainersDashboardUserDetails from "features/projects/retainers-dashboard/single/useRetainersDashboardUserDetails";
import { useProjectLeadAssignment } from "features/projects/project-dashboard/single/useProjectsDashboardDetailsData";
import usePageActionPermission from "shared/hooks/access/usePageActionPermission";
import SessionContext from "app/contexts/SessionContext";
import FormFeedbackNotificationText from "shared/components/feedback/error/FormFeedbackNotificationText";
import { useLeadProducerEmp } from "features/projects/project-dashboard/single/useProjectsDashboardDetails";

const ModalCTA = ({ onClose, hasPermission }) => {
    return (
        <ModalActions
            primaryAction={{
                type: "submit",
                label: "Confirm",
                disabled: !hasPermission,
            }}
            secondaryAction={{
                type: "button",
                label: "Cancel",
                onClick: onClose,
            }}
            as="fieldset"
        />
    );
};
function transformAgencyDataToFormData(projectId, projectsData = {}) {
    return {
        producerId: projectsData.EmployeeId,
        ProjectId: projectId,
        StartDate: new Date().toJSON(),
        IsProjectLead: true,
    };
}

function transformFormDataToAgencyData(
    CurrentEndDate,
    formData = {},
    leadProducerData = {}
) {
    const newProjectsData = { ...leadProducerData };

    newProjectsData.EmployeeId = formData.producerId;
    newProjectsData.ProjectId = formData.ProjectId;
    newProjectsData.StartDate = formData.StartDate;
    newProjectsData.EndDate = CurrentEndDate;
    newProjectsData.IsProjectLead = true;

    return newProjectsData;
}

const ProjectDashboardDetailsDataLeadProducerForm = ({
    projectIdToAddEditLead,
    leadProducerId,
    onClose,
}) => {
    const { projectId } = useParams();
    const { leadProducers } = useFilters();

    const session = useContext(SessionContext);
    const { projectByIdDetails } = useProjectByIdDetails();
    const { useUserById } = useRetainersDashboardUserDetails();
    const userDetails = useUserById(leadProducerId);
    const { createAssignment, useLeadAssignment } = useProjectLeadAssignment();
    const leadProducerAssignment = useLeadAssignment(projectId);
    const canUserEdit = leadProducerId
        ? session.legacyId === leadProducerId
        : true;
    const hasPermission = usePageActionPermission(
        "AC_PROJECT_EDIT_LEAD_PRODUCER",
        globalThis.config.routes.projectsDashboardLanding
    );
    const { leadProducerEmpIds } = useLeadProducerEmp();

    const leadProducerOptions = !leadProducers?.isLoading &&
        !leadProducerEmpIds.isLoading &&
        leadProducerEmpIds?.data?.length > 0
        ? getDropdownOptionsNoDefaultVal(leadProducers?.data.filter(f => leadProducerEmpIds.data.includes(f.id)), "id", "fullName")
        : [];

    if (leadProducerAssignment.isLoading) {
        return <div>Loading...</div>;
    }

    if (leadProducers.isError || leadProducerAssignment.isError || leadProducerEmpIds.isError) {
        return <div>Error</div>;
    }

    const isEditing = leadProducerId !== undefined;
    const modifier = createAssignment;

    const currentLeadProducerData = leadProducerAssignment?.data?.find(
        (leadProducer) => leadProducer.EmployeeId === leadProducerId
    );

    if (isEditing && !currentLeadProducerData) {
        return <div>Could not find lead Producer</div>;
    }

    const onSubmit = async (formData) => {
        const leadProducerData = transformFormDataToAgencyData(
            projectByIdDetails?.data?.CurrentEndDate,
            formData,
            currentLeadProducerData
        );

        const response = await modifier.mutateAsync(leadProducerData);
        onClose();
        sendNotification(
            <FormFeedbackNotificationText
                responseStatus={response?.status}
                isEditing={isEditing}
                name={"Lead Producer"}
            />
        );
    };

    return (
        <Form
            onSubmit={onSubmit}
            defaultValues={transformAgencyDataToFormData(
                projectId,
                currentLeadProducerData
            )}
        >
            <VStack spacing="Three">
                <VStack spacing="Two">
                    {isEditing && (
                        <ContactCard
                            contactCardLabel="Current Lead Producer"
                            contactCardLabelVariant="Body_2_1"
                            photoUrl={
                                env.BESTWORK_REACT_ASSET_DOMAIN +
                                "/" +
                                userDetails?.data?.photoURL
                            }
                            name={userDetails?.data?.fullName}
                            jobPost={userDetails?.data?.currentTitle}
                            department={userDetails?.data?.currentDepartment}
                            location={userDetails?.data?.currentOffice}
                        />
                    )}
                    <Dropdown
                        name="producerId"
                        validations={{
                            required: true,
                        }}
                        placeholder={"Select Lead Producer "}
                        disabled={!(hasPermission && canUserEdit) || leadProducers.isLoading || leadProducerEmpIds.isLoading}
                        label={
                            isEditing
                                ? "Updated Lead Producer"
                                : "Lead Producer for Project"
                        }
                        options={leadProducerOptions}
                    />
                </VStack>
                <ModalCTA
                    projectIdToAddEditLead={projectIdToAddEditLead}
                    onClose={onClose}
                    leadProducerId={leadProducerId}
                    isLoading={modifier.isLoading}
                    hasPermission={hasPermission && canUserEdit}
                />
            </VStack>
        </Form>
    );
};

ModalCTA.propTypes = {
    onClose: PropTypes.func.isRequired,
    hasPermission: PropTypes.bool
};

ProjectDashboardDetailsDataLeadProducerForm.propTypes = {
    projectIdToAddEditLead: PropTypes.string,
    leadProducerId: PropTypes.number,
    onClose: PropTypes.func.isRequired
};

export default ProjectDashboardDetailsDataLeadProducerForm;
